import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AdditionalInfo from "../../../components/Employees/edit/AdditionalInfo";
import CircularProgressBar from "../../../components/Employees/edit/CircularProgressBar";
import EmergencyInfo from "../../../components/Employees/edit/EmergencyInfo";
import FinancialInfo from "../../../components/Employees/edit/FinancialInfo";
import Item from "../../../components/Employees/edit/Item";
import OfficialInfo from "../../../components/Employees/edit/OfficialInfo";
import PersonalInfo from "../../../components/Employees/edit/PersonalInfo";
import SalaryInfo from "../../../components/Employees/edit/SalaryInfo";
import { calculateDetailsPercentage } from "../../../components/Employees/edit/utils";
import { useGetEmployeeDetailsQuery } from "../../../features/employee/employeeApi";
import {
  useGetAdditionalDataQuery,
  useGetEmployeeAdditionalFieldsQuery,
} from "../../../features/employeeAdditionalField/employeeAdditionalFieldApi";
import { useGetEmployeeBreakdownSalaryDetailsQuery } from "../../../features/employeeSalarySetting/employeeSalarySettingApi";
import useError from "../../../hooks/useError";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  employeeDetailsList,
  employeeDetailsListForEdit,
} from "./EmployeesDetails";

type ItemType = (typeof employeeDetailsList)[number];

export default function EditEmployees() {
  const { editId } = useParams();
  const [percentage, setPercentage] = useState(0);
  let [queryParams, setSearchParams] = useSearchParams();
  const [item, setItem] = useState<ItemType | number>("Official");
  const { data: getEmployeeData } = useGetEmployeeDetailsQuery(editId || "", {
    skip: !editId,
  });
  const { isLoading, isError, error, data } = useGetAdditionalDataQuery({});
  const {
    isError: isSalaryError,
    error: salaryError,
    data: salaryData,
  } = useGetEmployeeBreakdownSalaryDetailsQuery(editId || "", {
    skip: !editId,
  });
  const payrollSetting = salaryData?.data?.salary_info;
  const {
    isError: isAdditionalError,
    error: additionalError,
    data: additionalData,
  } = useGetEmployeeAdditionalFieldsQuery({
    params: { skip: 0, limit: 0 },
  });
  useError(
    isAdditionalError,
    additionalError,
    "Get additional information fail"
  );
  useError(isError, error, "Get additional data fail");
  useError(isSalaryError, salaryError, "Get salary setting fail");

  const employee = getEmployeeData?.data?.employee;

  function getInfoData() {
    if (item === "Official") {
      return <OfficialInfo />;
    } else if (item === "Personal") {
      return <PersonalInfo />;
    } else if (item === "Financial") {
      return <FinancialInfo />;
    } else if (item === "Salary") {
      return <SalaryInfo />;
    } else if (item === "Emergency") {
      return <EmergencyInfo />;
    } else if (checkAddition(item)) {
      return <AdditionalInfo />;
    } else {
      return undefined;
    }

    function checkAddition(value: ItemType | number) {
      const itemData = Number(value);
      return !isNaN(itemData);
    }
  }

  useEffect(() => {
    if (queryParams.get("type")) {
      setItem(queryParams.get("type") as ItemType);
    }
  }, [queryParams]);

  const payRoll = payrollSetting?.breakdown_percentage?.length
    ? payrollSetting?.breakdown_percentage
        ?.filter((item) => item?.is_active)
        ?.map((item) => ({
          ...item,
          amount: item?.amount || undefined,
        }))
    : payrollSetting?.global_gross_salary_component
        ?.filter((item) => item?.is_active)
        ?.map((item) => ({
          ...item,
          amount: undefined,
        }));

  const payrollObject = payRoll?.reduce((acc, item) => {
    acc[item.id] = item.amount;
    return acc;
  }, {});

  const detailsData = {
    Official: {
      name: employee?.member?.profile?.name,
      email: employee?.member?.profile?.email,
      department_id: employee?.role?.department?.id,
      business_role_id: employee?.role?.id,
      join_date: employee?.join_date,
      manager_id: employee?.manager_id,
      employee_id: employee?.employee_id,
      grade: employee?.grade,
      employee_type: employee?.employee_type,
    },
    // Personal: {
    //   gender: employee?.member?.profile?.gender,
    //   mobile: employee?.mobile,
    //   dob: employee?.member?.profile?.dob,
    //   address: employee?.member?.profile?.address,
    //   nationality: employee?.member?.profile?.nationality,
    //   nid_no: employee?.member?.profile?.nid_no,
    //   nid_image_back: employee?.member?.profile?.nid_image_back,
    //   nid_image_front: employee?.member?.profile?.nid_image_front,
    //   passport_no: employee?.member?.profile?.passport_no,
    //   passport_image: employee?.member?.profile?.passport_image,
    //   blood_group: employee?.member?.profile?.blood_group,
    //   social_links: {
    //     facebook: employee?.member?.social_links?.facebook,
    //     instagram: employee?.member?.social_links?.instagram,
    //     linkedIn: employee?.member?.social_links?.linkedIn,
    //   },
    // },
    // Financial: {
    //   tin_no: employee?.member?.profile?.tin_no,
    //   tin_certificate: employee?.member?.profile?.tin_certificate,
    //   bank_name: employee?.member?.profile?.bank_info?.bank_name,
    //   bank_account: employee?.member?.profile?.bank_info?.account_no,
    //   account_no: employee?.bkash_info?.account_no,
    // },
    Salary: {
      salary: payrollSetting?.global_gross_salary_component,
      ...payrollObject,
    },
    // Emergency: {
    //   emergency_contract_person_name:
    //     employee?.member?.emergency_contract_person_name,
    //   emergency_contract_person_number:
    //     employee?.member?.emergency_contract_person_number,
    //   emergency_contract_person_relationship:
    //     employee?.member?.emergency_contract_person_relationship,
    // },
    Additional: {
      currentData: employee?.additional_data?.length,
      allData: {
        additionalFields: new Array(
          additionalData?.data?.additionalFields?.length
        ),
      },
    },
  };

  const totalData = calculateDetailsPercentage(detailsData);

  useEffect(() => {
    const avgData =
      Object.values(totalData).reduce((acc, curr) => acc + curr, 0) /
      Object.values(totalData).length;
    setPercentage(Math.round(avgData || 0));
  }, [totalData]);

  return (
    <SidebarLayout title="Edit Employees">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-md p-6 border border-extra-light">
          <div className="flex justify-center items-center">
            <CircularProgressBar percentage={percentage} />
          </div>
          <p className="mt-4 mb-6 text-center text-display-body">
            Complete employee profile for better usage
          </p>

          <ul className="flex flex-col gap-2.5">
            {employeeDetailsListForEdit.map((el, index) => (
              <Item
                key={el}
                onClick={() => {
                  setItem(el);
                  setSearchParams({ type: el });
                }}
                active={item === el}
                completed={totalData?.[el] >= 80}
                index={index}
              >
                {el}
              </Item>
            ))}
            {isLoading ? (
              <li className="text-center py-1 text-body">Loading...</li>
            ) : (
              data?.data?.additionalSections?.map((el, index) => (
                <Item
                  key={el?.id}
                  onClick={() => {
                    setItem(el?.id);
                    setSearchParams({ type: el?.id?.toString() });
                  }}
                  active={item?.toString() === el?.id?.toString()}
                  completed={totalData?.Additional >= 80}
                  index={employeeDetailsListForEdit?.length + index}
                >
                  {el?.label}
                </Item>
              ))
            )}
          </ul>
        </div>

        <div className="bg-white md:col-span-2 rounded-md p-6 border border-extra-light">
          {getInfoData()}
        </div>
      </div>
    </SidebarLayout>
  );
}
